import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { ContactResponse } from "@guestbook/shared";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import { Badge } from "@/shared/components/ui/Badge";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/shared/components/ui/AlertDialog";
import { useContact, useDeleteContact } from "@/shared/lib/contacts";
import { useScopedHref } from "@/shared/lib/scope";

export function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const href = useScopedHref();
  const query = useContact(id);
  const del = useDeleteContact();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (query.isLoading) {
    return (
      <Section spacing="md">
        <Container size="4xl">
          <p className="type-body-base text-text-muted">Loading…</p>
        </Container>
      </Section>
    );
  }

  if (query.isError) {
    const notFound = query.error.status === 404;
    return (
      <Section spacing="md">
        <Container size="4xl">
          <div className="flex flex-col gap-4">
            <h1 className="type-heading-lg text-text">
              {notFound ? "Contact not found" : "Couldn't load contact"}
            </h1>
            <p className="type-body-base text-text-muted">
              {notFound
                ? "This contact may have been deleted or never existed."
                : query.error.message}
            </p>
            <div>
              <Button asChild variant="outline">
                <Link to={href("/app/contacts")}>Back to contacts</Link>
              </Button>
            </div>
          </div>
        </Container>
      </Section>
    );
  }

  const contact = query.data!;
  const fullName = contact.name || "Unnamed";

  function handleDelete() {
    if (!id) return;
    del.mutate(id, {
      onSuccess: () => navigate(href("/app/contacts")),
    });
  }

  return (
    <Section spacing="md">
      <Container size="4xl">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Link
              to={href("/app/contacts")}
              className="type-label-sm tracking-wider uppercase text-text-muted hover:text-text"
            >
              ← Back to contacts
            </Link>
            <div className="flex items-baseline justify-between gap-4">
              <h1 className="type-heading-lg text-text">{fullName}</h1>
              <div className="flex items-center gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to={href(`/app/contacts/${contact.id}/edit`)}>Edit</Link>
                </Button>
                <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost-danger" size="sm">
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {fullName}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes the contact and its tag links. The
                        action can't be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogAction asChild>
                        <Button
                          variant="destructive"
                          onClick={handleDelete}
                          disabled={del.isPending}
                        >
                          {del.isPending ? "Deleting…" : "Delete contact"}
                        </Button>
                      </AlertDialogAction>
                      <AlertDialogCancel asChild>
                        <Button variant="ghost">Cancel</Button>
                      </AlertDialogCancel>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>

          <ContactSummary contact={contact} />
        </div>
      </Container>
    </Section>
  );
}

function ContactSummary({ contact }: { contact: ContactResponse }) {
  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
      <section className="flex flex-col gap-4">
        <h2 className="type-heading-sm text-text">Contact</h2>
        <DefinitionRow label="Email" value={contact.email} />
        <DefinitionRow label="Phone" value={contact.phone} />
        {contact.household ? (
          <DefinitionRow
            label="Household"
            value={
              <Link
                to={`/app/contacts/households/${contact.household.id}`}
                className="text-brand underline-offset-4 hover:underline"
              >
                {contact.household.displayName ?? "Unnamed household"}
              </Link>
            }
          />
        ) : (
          <DefinitionRow label="Household" value={null} />
        )}
        {contact.tags.length > 0 ? (
          <div className="flex flex-col gap-2">
            <span className="type-label-sm tracking-wider uppercase text-text-muted">Tags</span>
            <div className="flex flex-wrap gap-2">
              {contact.tags.map((t) => (
                <Badge key={t}>{t}</Badge>
              ))}
            </div>
          </div>
        ) : null}
      </section>
      <section className="flex flex-col gap-4">
        <h2 className="type-heading-sm text-text">Mailing address</h2>
        {hasAddress(contact) ? (
          <address className="not-italic type-body-base text-text whitespace-pre-line">
            {[contact.address?.line1, contact.address?.line2].filter(Boolean).join("\n")}
            {contact.address?.line1 ? "\n" : ""}
            {[
              contact.address?.city,
              contact.address?.stateCode ?? contact.address?.stateName,
              contact.address?.postalCode,
            ]
              .filter(Boolean)
              .join(", ")}
            {contact.address?.countryName ?? contact.address?.countryCode
              ? `\n${contact.address?.countryName ?? contact.address?.countryCode}`
              : ""}
          </address>
        ) : (
          <p className="type-body-base text-text-muted">No address on file.</p>
        )}
        {contact.notes ? (
          <>
            <h2 className="type-heading-sm text-text mt-4">Notes</h2>
            <p className="type-body-base text-text whitespace-pre-line">{contact.notes}</p>
          </>
        ) : null}
      </section>
    </div>
  );
}

function hasAddress(contact: ContactResponse): boolean {
  const a = contact.address;
  if (!a) return false;
  return Boolean(
    a.line1 ||
      a.line2 ||
      a.city ||
      a.stateCode ||
      a.stateName ||
      a.postalCode ||
      a.countryCode ||
      a.countryName,
  );
}

function DefinitionRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="type-label-sm tracking-wider uppercase text-text-muted">{label}</span>
      <span className="type-body-base text-text">{value ?? "—"}</span>
    </div>
  );
}
